import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

// Angular Material imports
import { MatGridListModule } from '@angular/material/grid-list';
import { MatGridTile } from '@angular/material/grid-list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatDialog } from '@angular/material/dialog';
import { EmpresasService } from '../../../services/empresa/empresas.service';
import { PessoasService } from '../../../services/pessoa/pessoas.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { SelecionarEmpresaModal } from '../../../modals/selecionar-empresa-modal/selecionar-empresa-modal';
import { SelecionarEnderecoModal } from '../../../modals/selecionar-endereco-modal/selecionar-endereco-modal';
import { DesvincularEmpresa } from '../../pessoas/cadastrar-pessoa/dialogs/desvincular-empresa/desvincular-empresa';
import { DesvincularEndereco } from '../../pessoas/cadastrar-pessoa/dialogs/desvincular-endereco/desvincular-endereco';
import { UsuariosService } from '../../../services/usuario/usuarios.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../../services/auth/auth.service';
import { clearPaginas, pushPaginaAtiva } from '../../../shared/activePage';
import moment from 'moment';

interface Usuario {
  idUsuario?: number;
  naturalidade: string;
  tipoDocumento: string;
  numeroDocumento: string;
  nomeCompleto: string;
  dataNascimento: string;
  email: string;
  telefoneCelular: string;
  departamentoRegional: string;
  perfilDeAcesso: string;
  empresa: number;
  status: string;
  estabelecimento: number;
}

interface SelectOption {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-cadastrar-atualizar-usuario-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatGridTile,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    NgxMaskDirective,
    MatProgressSpinnerModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './cadastrar-atualizar-usuario-view.html',
  styleUrls: ['./cadastrar-atualizar-usuario-view.scss'],
})
export class CadastrarAtualizarUsuarioView implements OnInit {
  isLoading = false;
  idUsuario: number | null = null;
  isUsuarioLogado: boolean = false;
  modoEdicaoAtivo = false;

  // Opções para os selects
  naturalidadeOptions: SelectOption[] = [
    { value: '', viewValue: 'Selecione' },
    { value: 'Brasileiro', viewValue: 'Brasileiro' },
    { value: 'Estrangeiro', viewValue: 'Estrangeiro' },
  ];

  tipoDocumentoOptions: SelectOption[] = [
    { value: '', viewValue: 'Selecione' },
    { value: 'CPF', viewValue: 'CPF' },
    { value: 'Passaporte', viewValue: 'Passaporte' },
    { value: 'CRNM', viewValue: 'CRNM' },
    { value: 'CIE', viewValue: 'CIE' },
  ];

  filteredTipoDocumentoOptions: SelectOption[] = [...this.tipoDocumentoOptions];

  departamentoRegionalOptions: SelectOption[] = [
    { value: '', viewValue: 'Selecione' },
    { value: 'Departamento Nacional', viewValue: 'Departamento Nacional' },
    { value: 'Santa Catarina', viewValue: 'Santa Catarina' },
  ];

  perfilAcessoOptions: SelectOption[] = [
    { value: '', viewValue: 'Selecione' },
    { value: 'MASTER', viewValue: 'Master' },
    { value: 'GESTOR', viewValue: 'Gestor' },
    { value: 'SAUDE', viewValue: 'Saúde' },
  ];

  formData: Usuario = {
    naturalidade: '',
    tipoDocumento: '',
    numeroDocumento: '',
    nomeCompleto: '',
    dataNascimento: '',
    email: '',
    telefoneCelular: '',
    departamentoRegional: '',
    perfilDeAcesso: '',
    empresa: 0,
    status: '',
    estabelecimento: 0,
  };

  useCPF = signal(false);
  usuarioForm: FormGroup;
  isSaude = signal(false);
  podeInativar = signal(false);
  usuarioEstaPendente = signal(false);
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private readonly empresaService: EmpresasService,
    private readonly usuarioService: UsuariosService,
    private readonly snackbarService: SnackbarService,
    private readonly authService: AuthService
  ) {
    this.usuarioForm = this.fb.group({
      naturalidade: ['', Validators.required],
      tipoDocumento: ['', Validators.required],
      numeroDocumento: ['', Validators.required],
      nomeCompleto: ['', Validators.required],
      dataNascimento: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      telefoneCelular: ['', Validators.required],
      departamentoRegional: ['', Validators.required],
      perfilDeAcesso: ['', Validators.required],
      status: [''],
    });

    // Observa mudanças na naturalidade
    this.usuarioForm.get('naturalidade')?.valueChanges.subscribe((value) => {
      this.handleNaturalidadeChange(value);
    });
  }

  handleNaturalidadeChange(naturalidade: string): void {
    this.useCPF.set(naturalidade === 'Brasileiro');
    if (this.useCPF()) {
      this.formData.tipoDocumento = 'CPF';
      this.usuarioForm.get('tipoDocumento')?.setValue('CPF');

      this.filteredTipoDocumentoOptions = this.tipoDocumentoOptions.filter(
        (option) => option.value === 'CPF' || option.value === ''
      );
    } else {
      this.filteredTipoDocumentoOptions = [...this.tipoDocumentoOptions];

      if (this.formData.tipoDocumento === 'CPF') {
        this.formData.tipoDocumento = '';
        this.usuarioForm.get('tipoDocumento')?.setValue('');
      }
    }

    this.cd.detectChanges();
  }

  handleDocTipoChange(docTipo: string) {
    this.useCPF.set(docTipo === 'CPF');
  }

  async ngOnInit(): Promise<void> {
    this.isSaude.set(this.authService.isSaude());
    this.podeInativar.set(
      this.authService.isGestor() || this.authService.isMaster()
    );

    clearPaginas();
    pushPaginaAtiva({
        nome: 'Home',
        rota: '/home'
    });
    pushPaginaAtiva({
        nome: 'Usuários',
        rota: '/usuarios'
    });

    if (!!this.route.snapshot.paramMap.get('id')) {
      this.idUsuario = Number(this.route.snapshot.paramMap.get('id'));
      this.isUsuarioLogado = this.authService.getUsuarioId() === this.idUsuario;
      this.modoEdicaoAtivo = true;
      let nomeRota: string;
      this.isUsuarioLogado ? nomeRota = 'Meu perfil' : nomeRota = 'Visualizar usuário'
      pushPaginaAtiva({
        nome: nomeRota,
        rota: ''
      })
    } else {
      pushPaginaAtiva({
        nome: 'Cadastro de usuários',
        rota: ''
      })
    }

    try {
      this.isLoading = true;

      const res = await this.buscarDadosDoUsuarioParaAtualizar(this.idUsuario);

      this.empresasOptions =
        await this.empresaService.consultaTelaVincularEmpresaAPessoa();

      this.autoPreencherFormulario(res);

      if (this.modoEdicaoAtivo) {
        const empresa = this.empresasOptions?.find(
          (e) => e.idEmpresa === this.formData.empresa
        );
        const endereco = empresa?.enderecos?.find(
          (e: any) => e.idEstabelecimento === this.formData.estabelecimento
        );

        if (!empresa || !endereco) {
          this.snackbarService.error('Vínculo com a empresa não encontrados');
          return;
        }

        this.empresaSelecionada = empresa;
        this.enderecoSelecionado = endereco;
      }

      this.isLoading = false;
      this.usuarioEstaPendente.set(
        this.formData.status.toUpperCase() === 'PENDENTE'
      );
    } catch (err: any) {
      this.snackbarService.error('Erro ao carregar os dados');
    }
  }

  usuarioEstaInativo(): boolean {
    const status = this.formData.status;
    if (!status) return false;
    const statusUpper = status.toUpperCase();
    return statusUpper === 'INATIVO' || statusUpper === 'BLOQUEADO';
  }

  ngOnChanges(changes: SimpleChanges): void {}

  autoPreencherFormulario(usuario: Usuario | null): void {
    if (!usuario) return;

    this.formData = { ...usuario };
    this.usuarioForm.patchValue({
      naturalidade: usuario.naturalidade,
      tipoDocumento: usuario.tipoDocumento,
      numeroDocumento: usuario.numeroDocumento,
      nomeCompleto: usuario.nomeCompleto,
      dataNascimento: usuario.dataNascimento,
      email: usuario.email,
      status: usuario.status,
      empresa: usuario.empresa,
      estabelecimento: usuario.estabelecimento,
      telefoneCelular: usuario.telefoneCelular,
      departamentoRegional: usuario.departamentoRegional,
      perfilDeAcesso: this.perfilAcessoOptions.filter(
        (option) =>
          option.value.toUpperCase() === usuario.perfilDeAcesso.toUpperCase()
      )[0].value,
    });

    if (usuario.naturalidade === 'Brasileiro') {
      this.filteredTipoDocumentoOptions = this.tipoDocumentoOptions.filter(
        (option) => option.value === 'CPF' || option.value === ''
      );

      this.handleNaturalidadeChange(usuario.naturalidade);
    }
  }

  updateForm(field: keyof Usuario, value: any): void {
    if (field in this.formData) {
      (this.formData as any)[field] = value;

      const control = this.usuarioForm.get(field as string);
      if (control) {
        control.setValue(value);
        control.markAsDirty();
        control.updateValueAndValidity();
      }

      if (field === 'naturalidade') {
        this.handleNaturalidadeChange(value);
      }
      if (field === 'departamentoRegional') {
        this.formData.departamentoRegional = value;
      }
      if (field === 'perfilDeAcesso') {
        this.formData.perfilDeAcesso = value;
      }
    }
  }

  // SELECT EMPRESAS E ENDERECOS
  empresaSelecionada: any = null;
  enderecoSelecionado: any = null;
  empresasOptions: any[] = [];

  touchedEmpresa = false;
  empresaDummyModel = '';

  touchedEndereco = false;
  enderecoDummyModel = '';

  abrirModalEmpresas() {
    this.touchedEmpresa = true;
    const dialogRef = this.dialog.open(SelecionarEmpresaModal, {
      data: {
        empresas: this.empresasOptions,
        empresaSelecionada: this.empresaSelecionada,
      },
    });
    dialogRef.afterClosed().subscribe((empresa: any) => {
      if (empresa) this.empresaSelecionada = empresa;
    });
  }

  removeEmpresaSelecionada() {
    this.empresaSelecionada = null;
    this.enderecoSelecionado = null;
  }

  abrirModalEnderecos() {
    this.touchedEndereco = true;
    const dialogRef = this.dialog.open(SelecionarEnderecoModal, {
      data: {
        empresas: this.empresasOptions,
        empresaSelecionada: this.empresaSelecionada,
        enderecoSelecionado: this.enderecoSelecionado,
      },
    });

    dialogRef.afterClosed().subscribe((endereco: any) => {
      if (endereco) this.enderecoSelecionado = endereco;
    });
  }

  removeEnderecoSelecionado() {
    this.enderecoSelecionado = null;
  }

  abrirDesvincularEmpresa() {
    const dialogRef = this.dialog.open(DesvincularEmpresa, {
      width: '400px',
      data: {
        empresa: this.empresaSelecionada,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.removeEmpresaSelecionada();
        this.snackbarService.success('Empresa desvinculada com sucesso!');
      }
    });
  }

  abrirDesvincularEndereco() {
    const dialogRef = this.dialog.open(DesvincularEndereco, {
      width: '500px',
      data: {
        empresaSelecionada: this.empresaSelecionada,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result === true) {
        this.removeEnderecoSelecionado();
        this.snackbarService.success('Endereço desvinculado com sucesso');
      }
    });
  }

  onTouchedAll() {
    this.touchedEmpresa = true;
    this.touchedEndereco = true;
  }

  onCancel(): void {
    this.router.navigate(['/usuarios']);
  }

  async ativarUsuario() {
    if (this.idUsuario) {
      this.isLoading = true;
      try {
        await this.usuarioService.ativarUsuario(this.idUsuario);
        const res = await this.buscarDadosDoUsuarioParaAtualizar(
          this.idUsuario
        );
        this.autoPreencherFormulario(res);
        this.snackbarService.success('Usuário inativado com sucesso!');
        this.router.navigate(['/usuarios']);
        this.isLoading = false;
      } catch (err: any) {
        this.snackbarService.error('Ocorreu um erro ao inativar o usuário');
        this.isLoading = false;
      }
    }
  }

  async inativarUsuario() {
    if (this.idUsuario) {
      this.isLoading = true;
      try {
        await this.usuarioService.inativarUsuario(this.idUsuario);
        const res = await this.buscarDadosDoUsuarioParaAtualizar(
          this.idUsuario
        );
        this.autoPreencherFormulario(res);
        this.snackbarService.success('Usuário inativado com sucesso!');
        this.router.navigate(['/usuarios']);
        this.isLoading = false;
      } catch (err: any) {
        this.snackbarService.error('Ocorreu um erro ao inativar o usuário');
        this.isLoading = false;
      }
    }
  }

  obterIdUsuarioDaRota() {
    return Number(this.route.snapshot.paramMap.get('id')) ?? null;
  }

  async onSubmit() {
    if (!this.empresaSelecionada) {
      this.touchedEmpresa = true;
      this.snackbarService.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (!this.enderecoSelecionado) {
      this.touchedEndereco = true;
      this.snackbarService.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (this.usuarioForm.valid) {
      const formattedValues = {
        ...this.usuarioForm.value,
        telefoneCelular: this.getFormattedPhone(
          this.usuarioForm.value.telefoneCelular
        ),
        dataNascimento: this.formatDate(this.usuarioForm.value.dataNascimento),
        numeroDocumento: this.getFormattedDocument(
          this.usuarioForm.value.numeroDocumento,
          this.usuarioForm.value.tipoDocumento
        ),
      };

      const usuarioCompleto: Usuario = {
        ...this.formData,
        ...formattedValues,
        empresa: this.empresaSelecionada.idEmpresa,
        estabelecimento: this.enderecoSelecionado.idEstabelecimento,
      };

      const body = {
        id: this.obterIdUsuarioDaRota(),
        nomeCompleto: usuarioCompleto.nomeCompleto,
        email: usuarioCompleto.email,
        telefoneCelular: this.getFormattedPhone(
          usuarioCompleto.telefoneCelular
        ),
        dataNascimento: this.formatDate(this.usuarioForm.value.dataNascimento),
        tipoDocumento: usuarioCompleto.tipoDocumento,
        numeroDocumento: this.getFormattedDocument(
          usuarioCompleto.numeroDocumento,
          usuarioCompleto.tipoDocumento
        ),
        naturalidade: usuarioCompleto.naturalidade,
        perfilDeAcesso: usuarioCompleto.perfilDeAcesso.trim().toUpperCase(),
        empresa: this.empresaSelecionada.idEmpresa,
        estabelecimento: this.enderecoSelecionado.idEstabelecimento,
        departamentoRegional: usuarioCompleto.departamentoRegional,
      };

      this.isLoading = true;
      try {
        await this.usuarioService.salvarOuAtualizarUsuario(body);
        this.snackbarService.success('Usuário salvo com sucesso!');
        this.isLoading = false;
        this.router.navigate(['/usuarios']);
      } catch (err: any) {
        this.snackbarService.error(
          err.response.data.message ?? 'Ocorreu um erro ao salvar usuário'
        );
        this.isLoading = false;
      }
    } else {
      this.usuarioForm.markAllAsTouched();
      this.snackbarService.error('Preencha todos os campos obrigatórios');
    }
  }

  // Métodos auxiliares para formatação
  private getFormattedPhone(phone: string): string {
    if (!phone) return '';

    const cleaned = phone.replace(/\D/g, '');

    // Aplica a máscara (00) 00000-0000
    return cleaned.length === 11
      ? `(${cleaned.substring(0, 2)}) ${cleaned.substring(
          2,
          7
        )}-${cleaned.substring(7)}`
      : phone;
  }

  private formatDate(date: string | Date): string {
    if (!date) return '';
    return moment(date).format('DD-MM-YYYY');
  }

  private getFormattedDocument(doc: string, docType: string): string {
    if (!doc) return '';

    const cleaned = doc.replace(/\D/g, '');

    switch (docType) {
      case 'CPF':
        return cleaned.length === 11
          ? `${cleaned.substring(0, 3)}.${cleaned.substring(
              3,
              6
            )}.${cleaned.substring(6, 9)}-${cleaned.substring(9)}`
          : doc;

      default:
        return doc;
    }
  }

  async buscarDadosDoUsuarioParaAtualizar(id: number | null) {
    if (id === null) {
      return;
    }

    try {
      this.isLoading = true;
      const res =
        await this.usuarioService.buscarDadosDoUsuarioParaTelaDeAtualizacao(id);

      this.isLoading = false;
      return res.data;
    } catch (err: any) {
      this.snackbarService.error(
        'Ocorreu um erro ao carregar os dados do usuário selecionado'
      );
      this.isLoading = false;
    }
  }
}
